import { I18n } from '@affine/i18n';
import {
  packColor,
  type PickColorEvent,
} from '@blocksuite/affine-components/color-picker';
import {
  type Color,
  DefaultTheme,
  resolveColor,
} from '@blocksuite/affine-model';
import type {
  ToolbarActions,
  ToolbarContext,
} from '@blocksuite/affine-shared/services';
import { BlockModel } from '@blocksuite/store';
import { signal } from '@preact/signals-core';
import { html } from 'lit';

type TextStyleBlockModel = BlockModel & {
  props: {
    fontSize: number;
    textColor: Color;
  };
};

type TextStyleOptions<T extends TextStyleBlockModel> = {
  getModels: (ctx: ToolbarContext) => T[];
  fallbackColor: (model: T, ctx: ToolbarContext) => Color;
};

const FONT_SIZES = [
  { value: 16 },
  { value: 18 },
  { value: 21 },
  { value: 24 },
  { value: 32 },
  { value: 40 },
] as const;

export function createCanvasTextStyleActions<T extends TextStyleBlockModel>({
  fallbackColor,
  getModels,
}: TextStyleOptions<T>) {
  return [
    {
      id: 'b.text-color',
      content(ctx) {
        const models = getModels(ctx);
        if (!models.length) return null;

        const theme = ctx.theme.edgeless$.value;
        const originalColor =
          models[0].props.textColor || fallbackColor(models[0], ctx);
        const color = resolveColor(originalColor, theme);
        const onPick = (event: PickColorEvent) => {
          if (event.type === 'start') {
            ctx.store.captureSync();
            return;
          }
          if (event.type !== 'pick') return;

          const props = packColor('textColor', event.detail.value);
          models.forEach(model => ctx.store.updateBlock(model, props));
        };

        return html`<edgeless-color-picker-button
          class="text-color"
          .label=${I18n.t(
            'com.streamlain.settings.editorSettings.edgeless.shape.text-color'
          )}
          .pick=${onPick}
          .color=${color}
          .theme=${theme}
          .isText=${true}
          .hollowCircle=${true}
          .originalColor=${originalColor}
          .palettes=${DefaultTheme.ShapeTextColorPalettes}
          .enableCustomColor=${ctx.features.getFlag('enable_color_picker')}
        ></edgeless-color-picker-button>`;
      },
    },
    {
      id: 'c.font-size',
      content(ctx) {
        const models = getModels(ctx);
        if (!models.length) return null;

        const fontSize$ = signal(Math.round(models[0].props.fontSize || 21));
        const onSelect = (event: CustomEvent<number>) => {
          event.stopPropagation();
          models.forEach(model =>
            ctx.store.updateBlock(model, { fontSize: event.detail })
          );
        };

        return html`<affine-size-dropdown-menu
          @select=${onSelect}
          .label=${I18n.t(
            'com.streamlain.settings.editorSettings.edgeless.shape.font-size'
          )}
          .minSize=${10}
          .maxSize=${96}
          .sizes=${FONT_SIZES}
          .size$=${fontSize$}
        ></affine-size-dropdown-menu>`;
      },
    },
  ] as const satisfies ToolbarActions;
}
